import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus, Edit2, Trash2, X, ChefHat, DollarSign, Eye, EyeOff, Star,
  UtensilsCrossed,
  Upload, ImagePlus, GripVertical, Check,
  History, ChevronDown, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import VoiceInput from '../components/VoiceInput';
import ModifierManager, { revealModifierOption } from '../components/ModifierManager';
import UiSearchField from '../components/ui/UiSearchField';
import UiDateField from '../components/ui/UiDateField';
import UiTimeField from '../components/ui/UiTimeField';
import useSortable from '../hooks/useSortable';
import SortableHeader from '../components/SortableHeader';
import { getRecipeComponents, getModifiers } from '../api/modifiers';
import { formatApiError } from '../utils/apiErrors';
import { formatUiCurrencyCompact, formatUiCurrencyDetailed, formatUiDateTime } from '../utils/uiFormat';
import { UiSummaryCard, UiSummaryCards } from '../components/ui/UiSummaryCard';
import { UiTabs } from '../components/ui/UiTabs';

const MENU_SHOW_UNAVAILABLE_ITEMS_KEY = 'menu.showUnavailableItems';
const MENU_SHOW_UNAVAILABLE_MODIFIERS_KEY = 'menu.showUnavailableModifiers';

const readStoredToggle = (key, fallback = true) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    return stored == null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
};

export const formatCloneOptionLabel = (item = {}) => (
  `${item.name || ''}${item.category_name ? ` (${item.category_name})` : ''}`
);

const MenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [servingTypes, setServingTypes] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [recipeComponents, setRecipeComponents] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const menuModifierRowRefs = useRef([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [showServingModal, setShowServingModal] = useState(false);
  const [showPriceHistoryModal, setShowPriceHistoryModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [costModalItem, setCostModalItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPriceHistoryItem, setSelectedPriceHistoryItem] = useState(null);
  const [priceHistoryData, setPriceHistoryData] = useState({ prices: [], costs: [] });
  const [expandedPriceHistoryRows, setExpandedPriceHistoryRows] = useState(new Set());
  // Historical price entry (single item)
  const [showAddHistoryForm, setShowAddHistoryForm] = useState(false);
  const [historyEntryType, setHistoryEntryType] = useState('price'); // 'price' | 'cost'
  const [historyEntryDate, setHistoryEntryDate] = useState('');
  const [historyEntryTime, setHistoryEntryTime] = useState('12:00');
  const [historyEntryValue, setHistoryEntryValue] = useState('');
  const [historyEntrySubmitting, setHistoryEntrySubmitting] = useState(false);
  // Bulk historical price entry
  const [showBulkPriceHistory, setShowBulkPriceHistory] = useState(false);
  const [bulkHistoryDate, setBulkHistoryDate] = useState('');
  const [bulkHistoryTime, setBulkHistoryTime] = useState('12:00');
  const [bulkHistoryEntries, setBulkHistoryEntries] = useState([]);
  const [bulkHistoryModified, setBulkHistoryModified] = useState(new Set());
  const [bulkHistorySubmitting, setBulkHistorySubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', category: '', serving_type: '', selling_price: 0, description: '', is_available: true
  });
  const [itemImage, setItemImage] = useState(null);
  const [clonedItemImageUrl, setClonedItemImageUrl] = useState('');
  const [clonedItemImageName, setClonedItemImageName] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [baseComponents, setBaseComponents] = useState([]);
  const [portionConfigs, setPortionConfigs] = useState([]);
  const [selectedPortionOptionId, setSelectedPortionOptionId] = useState('');
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', department: '' });
  const [catImage, setCatImage] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });
  const [editingDept, setEditingDept] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingServing, setEditingServing] = useState(null);
  const [servingForm, setServingForm] = useState({ name: '', description: '', is_active: true, sort_order: 0 });
  const [servingOptions, setServingOptions] = useState([{ name: '', description: '', is_active: true, sort_order: 0 }]);
  const [dragIdx, setDragIdx] = useState(null);
  const [showCategoryOrderModal, setShowCategoryOrderModal] = useState(false);
  const [compositionEditTarget, setCompositionEditTarget] = useState(null);
  const [setupTab, setSetupTab] = useState('servings');
  const [showUnavailableMenuItems, setShowUnavailableMenuItems] = useState(() => readStoredToggle(MENU_SHOW_UNAVAILABLE_ITEMS_KEY, true));
  const [showUnavailableModifierItems, setShowUnavailableModifierItems] = useState(() => readStoredToggle(MENU_SHOW_UNAVAILABLE_MODIFIERS_KEY, true));
  const [itemCloneSourceId, setItemCloneSourceId] = useState('');
  const [itemSectionsExpanded, setItemSectionsExpanded] = useState({
    base: false,
    modifiers: false,
    ingredients: false,
  });

  const { hasPermission } = useAuthStore();
  const [pageTab, setPageTab] = useState('items');

  const getCategoryIcon = () => ChefHat;

  const createEmptyMenuModifierLink = (sortOrder = 0) => ({
    modifier_group_id: '',
    is_required: false,
    allow_multiple_quantities: false,
    min_selections: 0,
    max_selections: 1,
    sort_order: sortOrder,
  });

  const normalizeIngredientEntry = (ingredient = {}) => ({
    raw_material_id: ingredient.raw_material_id || ingredient.raw_material || '',
    quantity: ingredient.quantity ?? 0,
    name: ingredient.name || ingredient.raw_material_name || '',
  });

  const normalizeBaseComponentEntry = (component = {}) => ({
    recipe_component_id: component.recipe_component_id || component.recipe_component || '',
    quantity: component.quantity ?? 1,
    sort_order: component.sort_order ?? 0,
  });

  const createEmptyPortionConfig = (option, fallbackSortOrder = 0) => ({
    serving_size_option_id: option.id,
    serving_size_option_name: option.name,
    price: '',
    is_available: true,
    sort_order: option.sort_order ?? fallbackSortOrder,
    ingredients: [],
    base_components: [],
  });

  const selectedServingType = useMemo(
    () => servingTypes.find(serving => String(serving.id) === String(formData.serving_type)) || null,
    [servingTypes, formData.serving_type]
  );

  const servingTypeOptions = useMemo(
    () => [...(selectedServingType?.options || [])].sort((a, b) => {
      const aOrder = Number.isFinite(parseInt(a.sort_order, 10)) ? parseInt(a.sort_order, 10) : 0;
      const bOrder = Number.isFinite(parseInt(b.sort_order, 10)) ? parseInt(b.sort_order, 10) : 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    }),
    [selectedServingType]
  );

  const selectedPortionConfig = useMemo(
    () => portionConfigs.find(config => String(config.serving_size_option_id) === String(selectedPortionOptionId)) || null,
    [portionConfigs, selectedPortionOptionId]
  );

  const getPortionConfigStatus = (config) => {
    if (!config) return 'incomplete';
    const hasPrice = String(config.price ?? '').trim() !== '';
    return hasPrice ? 'configured' : 'incomplete';
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(MENU_SHOW_UNAVAILABLE_ITEMS_KEY, String(showUnavailableMenuItems));
    } catch {}
  }, [showUnavailableMenuItems]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(MENU_SHOW_UNAVAILABLE_MODIFIERS_KEY, String(showUnavailableModifierItems));
    } catch {}
  }, [showUnavailableModifierItems]);

  // Refresh modifiers and components when switching back to Items tab
  // so newly created modifiers/components appear without full page refresh
  useEffect(() => {
    if (pageTab === 'items') {
      Promise.all([getRecipeComponents(), getModifiers()]).then(([componentRes, modifierRes]) => {
        setRecipeComponents(componentRes.data.results || componentRes.data);
        setModifiers(modifierRes.data.results || modifierRes.data);
      }).catch(() => {});
    }
  }, [pageTab]);

  useEffect(() => {
    if (!formData.serving_type) {
      setSelectedPortionOptionId('');
      return;
    }
    if (servingTypeOptions.length === 0) {
      setSelectedPortionOptionId('');
      setPortionConfigs([]);
      return;
    }

    setPortionConfigs(prev => {
      const previousMap = new Map(prev.map(config => [String(config.serving_size_option_id), config]));
      return servingTypeOptions.map((option, index) => {
        const existing = previousMap.get(String(option.id));
        if (existing) {
          return {
            ...existing,
            serving_size_option_id: option.id,
            serving_size_option_name: option.name,
            sort_order: option.sort_order ?? existing.sort_order ?? index,
            ingredients: (existing.ingredients || []).map(normalizeIngredientEntry),
            base_components: (existing.base_components || []).map(normalizeBaseComponentEntry),
          };
        }
        return createEmptyPortionConfig(option, index);
      });
    });

    setSelectedPortionOptionId((current) => (
      servingTypeOptions.some(option => String(option.id) === String(current))
        ? current
        : String(servingTypeOptions[0].id)
    ));
  }, [formData.serving_type, servingTypeOptions]);

  const fetchData = async () => {
    try {
      const [menuRes, catRes, rawRes, deptRes, servingRes] = await Promise.all([
        api.get('/menu-items/?page_size=500'),
        api.get('/categories/?page_size=500'),
        api.get('/raw-materials/?active=true&page_size=500'),
        api.get('/departments/?page_size=500'),
        api.get('/serving-types/?page_size=500')
      ]);
      const [componentRes, modifierRes] = await Promise.all([
        getRecipeComponents(),
        getModifiers(),
      ]);
      setMenuItems(menuRes.data.results || menuRes.data);
      setCategories(catRes.data.results || catRes.data);
      setRawMaterials(rawRes.data.results || rawRes.data);
      setDepartments(deptRes.data.results || deptRes.data);
      setServingTypes(servingRes.data.results || servingRes.data);
      setRecipeComponents(componentRes.data.results || componentRes.data);
      setModifiers(modifierRes.data.results || modifierRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(formatApiError(error, 'Failed to load menu data'));
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    const matchesAvailability = showUnavailableMenuItems || item.is_available !== false;
    return matchesCategory && matchesAvailability;
  }).sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || String(a.name || '').localeCompare(String(b.name || '')));

  const visibleModifierGroups = useMemo(
    () => modifiers.filter((group) => (
      showUnavailableModifierItems
        || (group.options || []).some((option) => option.is_available !== false)
    )),
    [modifiers, showUnavailableModifierItems]
  );

  const toggleItemSection = (sectionKey) => {
    setItemSectionsExpanded((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const updatePortionConfig = (servingSizeOptionId, updater) => {
    setPortionConfigs(prev => prev.map((config) => {
      if (String(config.serving_size_option_id) !== String(servingSizeOptionId)) {
        return config;
      }
      const nextConfig = typeof updater === 'function' ? updater(config) : updater;
      return {
        ...config,
        ...nextConfig,
        ingredients: (nextConfig.ingredients ?? config.ingredients ?? []).map(normalizeIngredientEntry),
        base_components: (nextConfig.base_components ?? config.base_components ?? []).map(normalizeBaseComponentEntry),
      };
    }));
  };

  const currentIngredients = formData.serving_type
    ? (selectedPortionConfig?.ingredients || [])
    : ingredients;

  const currentBaseComponents = formData.serving_type
    ? (selectedPortionConfig?.base_components || [])
    : baseComponents;

  // Modifiers are per-portion for serving-size items, global otherwise
  const currentModifiers = formData.serving_type
    ? (selectedPortionConfig?.modifiers || [])
    : selectedModifiers;

  const currentPrice = formData.serving_type
    ? (selectedPortionConfig?.price ?? '')
    : formData.selling_price;

  const currentAvailability = formData.serving_type
    ? (selectedPortionConfig?.is_available !== false)
    : formData.is_available;

  const setCurrentIngredients = (nextIngredients) => {
    const normalized = nextIngredients.map(normalizeIngredientEntry);
    if (formData.serving_type && selectedPortionConfig) {
      updatePortionConfig(selectedPortionConfig.serving_size_option_id, { ingredients: normalized });
      return;
    }
    setIngredients(normalized);
  };

  const setCurrentBaseComponents = (nextBaseComponents) => {
    const normalized = nextBaseComponents.map(normalizeBaseComponentEntry);
    if (formData.serving_type && selectedPortionConfig) {
      updatePortionConfig(selectedPortionConfig.serving_size_option_id, { base_components: normalized });
      return;
    }
    setBaseComponents(normalized);
  };

  const setCurrentPrice = (nextPrice) => {
    if (formData.serving_type && selectedPortionConfig) {
      updatePortionConfig(selectedPortionConfig.serving_size_option_id, { price: nextPrice });
      return;
    }
    setFormData(prev => ({ ...prev, selling_price: nextPrice }));
  };

  const setCurrentAvailability = (nextAvailability) => {
    if (formData.serving_type && selectedPortionConfig) {
      updatePortionConfig(selectedPortionConfig.serving_size_option_id, { is_available: nextAvailability });
      return;
    }
    setFormData(prev => ({ ...prev, is_available: nextAvailability }));
  };

  // Write modifiers to the selected portion config (serving items) or global list (simple items)
  const setCurrentModifiers = (updater) => {
    if (formData.serving_type && selectedPortionConfig) {
      const prev = selectedPortionConfig.modifiers || [];
      const next = typeof updater === 'function' ? updater(prev) : updater;
      updatePortionConfig(selectedPortionConfig.serving_size_option_id, { modifiers: next });
      return;
    }
    setSelectedModifiers((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  };

  const addMenuModifierRow = () => {
    const nextIndex = currentModifiers.length;
    setCurrentModifiers((prev) => [
      ...prev,
      createEmptyMenuModifierLink(prev.length),
    ]);
    revealModifierOption(() => menuModifierRowRefs.current[nextIndex]);
  };

  const updateMenuModifierRow = (index, field, value) => {
    setCurrentModifiers((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )));
  };

  const removeMenuModifierRow = (index) => {
    setCurrentModifiers((prev) => prev
      .filter((_, rowIndex) => rowIndex !== index)
      .map((row, rowIndex) => ({ ...row, sort_order: rowIndex })));
  };

  const { sortKey: searchSortKey, sortDir: searchSortDir, handleSort: handleSearchSort, sortData: sortSearchData } = useSortable('name', 'asc');
  const { sortKey: priceSortKey, sortDir: priceSortDir, handleSort: handlePriceSort, sortData: sortPriceData } = useSortable('effective_from', 'desc');
  const { sortKey: costSortKey, sortDir: costSortDir, handleSort: handleCostSort, sortData: sortCostData } = useSortable('effective_from', 'desc');

  const sortedSearchResults = useMemo(
    () => sortSearchData(searchResults, (row, sortKey) => row?.[sortKey]),
    [searchResults, sortSearchData]
  );
  const sortedPriceHistory = useMemo(
    () => sortPriceData(priceHistoryData.prices || [], (row, sortKey) => row?.[sortKey]),
    [priceHistoryData.prices, sortPriceData]
  );
  const sortedCostHistory = useMemo(
    () => sortCostData(priceHistoryData.costs || [], (row, sortKey) => row?.[sortKey]),
    [priceHistoryData.costs, sortCostData]
  );

  const menuPageSummary = useMemo(() => {
    const filteredCount = filteredItems.length;
    const availableCount = filteredItems.filter(item => item.is_available).length;
    const favoriteCount = filteredItems.filter(item => item.is_favorite).length;
    const averagePrice = filteredCount
      ? filteredItems.reduce((sum, item) => sum + parseFloat(item.selling_price || 0), 0) / filteredCount
      : 0;
    const averageCost = filteredCount
      ? filteredItems.reduce((sum, item) => sum + parseFloat((item.runtime_cost_price ?? item.cost_price) || 0), 0) / filteredCount
      : 0;
    const activeModifierCount = modifiers.filter(modifier => modifier.is_active !== false).length;
    const modifierOptionCount = modifiers.reduce((sum, modifier) => sum + ((modifier.options || []).length), 0);
    const componentLinkedItems = menuItems.filter(item => (item.base_components || []).length > 0).length;

    return {
      setup: [
        { label: 'Departments', value: departments.length, tone: 'text-gray-800' },
        { label: 'Categories', value: categories.length, tone: 'text-gray-800' },
        { label: 'Serving Sizes', value: servingTypes.length, tone: 'text-gray-800' },
        { label: 'Menu Items', value: menuItems.length, tone: 'text-primary-600' },
      ],
      items: [
        { label: 'Items in View', value: filteredCount, tone: 'text-gray-800' },
        { label: 'Available', value: availableCount, tone: 'text-green-700' },
        { label: 'Favorites', value: favoriteCount, tone: 'text-amber-700' },
        { label: 'Average Price', value: formatUiCurrencyCompact(averagePrice), tone: 'text-primary-600' },
        { label: 'Average Cost', value: formatUiCurrencyCompact(averageCost), tone: 'text-rose-700' },
      ],
      modifiers: [
        { label: 'Modifier Groups', value: modifiers.length, tone: 'text-gray-800' },
        { label: 'Active Groups', value: activeModifierCount, tone: 'text-green-700' },
        { label: 'Options', value: modifierOptionCount, tone: 'text-primary-600' },
      ],
      components: [
        { label: 'Components', value: recipeComponents.length, tone: 'text-gray-800' },
        { label: 'Raw Materials', value: rawMaterials.length, tone: 'text-primary-600' },
        { label: 'Items Using Base Components', value: componentLinkedItems, tone: 'text-green-700' },
      ],
    };
  }, [categories, departments, filteredItems, menuItems, modifiers, rawMaterials, recipeComponents, servingTypes]);

  const uploadImage = async (modelType, objectId, file, fieldName = 'image') => {
    const fd = new FormData();
    fd.append('model_type', modelType);
    fd.append('object_id', objectId);
    fd.append('image', file);
    fd.append('field_name', fieldName);
    await api.post('/upload-image/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const uploadClonedItemImage = async (itemId, imageUrl, itemName) => {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error('Failed to download source image');
    }
    const blob = await response.blob();
    const safeBaseName = String(itemName || 'menu-item')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'menu-item';
    const extension = (blob.type && blob.type.split('/')[1]) || 'jpg';
    const clonedFile = new File([blob], `${safeBaseName}.${extension}`, { type: blob.type || 'image/jpeg' });
    await uploadImage('menu_item', itemId, clonedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const isServingItem = Boolean(formData.serving_type);
      const normalizedModifierGroups = selectedModifiers
        .filter((link) => link.modifier_group_id)
        .map((link, index) => ({
          modifier_group_id: link.modifier_group_id,
          is_required: link.is_required || false,
          allow_multiple_quantities: link.allow_multiple_quantities || false,
          min_selections: link.min_selections ?? 0,
          max_selections: link.max_selections ?? 1,
          sort_order: link.sort_order ?? index,
        }));
      if (isServingItem) {
        if (servingTypeOptions.length === 0) {
          toast.error('Define portions for this serving size before saving the item');
          return;
        }
        const missingPricePortion = portionConfigs.find((config) => String(config.price ?? '').trim() === '');
        if (missingPricePortion) {
          toast.error(`Enter the price for ${missingPricePortion.serving_size_option_name || 'every portion'}`);
          setSelectedPortionOptionId(String(missingPricePortion.serving_size_option_id || ''));
          return;
        }
      }
      const normalizedPortionConfigs = isServingItem
        ? portionConfigs.map((config) => ({
            serving_size_option_id: config.serving_size_option_id,
            price: config.price || 0,
            is_available: config.is_available !== false,
            sort_order: config.sort_order ?? 0,
            ingredients: (config.ingredients || []).map((ingredient) => ({
              raw_material_id: ingredient.raw_material_id,
              quantity: ingredient.quantity,
            })),
            base_components: (config.base_components || []).map((component) => ({
              recipe_component_id: component.recipe_component_id,
              quantity: component.quantity,
              sort_order: component.sort_order ?? 0,
            })),
            modifier_groups: (config.modifiers || [])
              .filter((link) => link.modifier_group_id)
              .map((link, index) => ({
                modifier_group_id: link.modifier_group_id,
                is_required: link.is_required || false,
                allow_multiple_quantities: link.allow_multiple_quantities || false,
                min_selections: link.min_selections ?? 0,
                max_selections: link.max_selections ?? 1,
                sort_order: link.sort_order ?? index,
              })),
          }))
        : [];

      const servingHasModifiers = isServingItem
        && normalizedPortionConfigs.some(config => (config.modifier_groups || []).length > 0);
      const data = {
        ...formData,
        serving_type: formData.serving_type || null,
        selling_price: isServingItem ? (normalizedPortionConfigs[0]?.price || 0) : formData.selling_price,
        is_available: isServingItem ? normalizedPortionConfigs.some(config => config.is_available !== false) : formData.is_available,
        ingredients: isServingItem ? [] : ingredients,
        has_modifiers: isServingItem ? servingHasModifiers : normalizedModifierGroups.length > 0,
        base_components_data: isServingItem ? [] : baseComponents,
        modifier_groups_data: isServingItem ? [] : normalizedModifierGroups,
        portion_configs_data: normalizedPortionConfigs,
      };
      let res;
      if (selectedItem) {
        res = await api.patch(`/menu-items/${selectedItem.id}/`, data);
        toast.success('Menu item updated');
      } else {
        res = await api.post('/menu-items/', data);
        toast.success('Menu item added');
      }
      const itemId = res.data.id || selectedItem?.id;
      if (itemId) {
        if (itemImage) {
          await uploadImage('menu_item', itemId, itemImage);
        } else if (!selectedItem && clonedItemImageUrl) {
          try {
            await uploadClonedItemImage(itemId, clonedItemImageUrl, formData.name);
          } catch (imageError) {
            toast.error(formatApiError(imageError, 'Menu item saved but image could not be cloned'));
          }
        }
      }
      setShowModal(false);
      resetForm();
      setItemImage(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save menu item'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await api.delete(`/menu-items/${id}/`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete item'));
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await api.post(`/menu-items/${item.id}/toggle_availability/`);
      toast.success(`${item.name} is now ${item.is_available ? 'unavailable' : 'available'}`);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update availability'));
    }
  };

  const toggleFavorite = async (item) => {
    try {
      await api.post(`/menu-items/${item.id}/toggle_favorite/`);
      toast.success(`${item.name} ${item.is_favorite ? 'removed from' : 'added to'} favorites`);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update favorite status'));
    }
  };

  const applyItemFormState = (item, { editing = false } = {}) => {
    setSelectedItem(editing ? item : null);
    // Map a modifier link from API → form row
    const mapModifierLink = (link) => ({
      modifier_group_id: link.modifier,
      is_required: link.is_required || false,
      allow_multiple_quantities: link.allow_multiple_quantities || false,
      min_selections: link.min_selections ?? 0,
      max_selections: link.max_selections ?? 1,
      sort_order: link.sort_order ?? 0,
    });
    const allModifierLinks = item.modifiers || [];
    const itemPortionConfigs = (item.portion_configs || []).map((config) => ({
      id: config.id,
      serving_size_option_id: config.serving_size_option || config.serving_size_option_id,
      serving_size_option_name: config.serving_size_option_name || config.serving_size_option_detail?.name || '',
      price: config.price ?? '',
      is_available: config.is_available !== false,
      sort_order: config.sort_order ?? 0,
      ingredients: (config.recipe_ingredients || []).map(normalizeIngredientEntry),
      base_components: (config.base_components || []).map(normalizeBaseComponentEntry),
      // Per-portion modifiers: links whose portion_config matches this portion
      modifiers: allModifierLinks
        .filter(link => String(link.portion_config) === String(config.id))
        .map(mapModifierLink),
    }));
    setFormData({
      name: item.name,
      category: item.category || '',
      serving_type: item.serving_type || '',
      selling_price: item.selling_price,
      description: item.description || '',
      is_available: item.is_available
    });
    setIngredients(item.recipe_ingredients?.map(i => ({
      raw_material_id: i.raw_material,
      quantity: i.quantity,
      name: i.raw_material_name
    })) || []);
    setBaseComponents(item.base_components?.map(component => ({
      recipe_component_id: component.recipe_component,
      quantity: component.quantity,
      sort_order: component.sort_order || 0,
    })) || []);
    // Global (non-portion) modifiers go to the flat list for simple items
    setSelectedModifiers(allModifierLinks
      .filter(link => !link.portion_config)
      .map(mapModifierLink));
    setPortionConfigs(itemPortionConfigs);
    setSelectedPortionOptionId(
      itemPortionConfigs[0]?.serving_size_option_id
        ? String(itemPortionConfigs[0].serving_size_option_id)
        : (item.serving_type_detail?.options?.[0]?.id ? String(item.serving_type_detail.options[0].id) : '')
    );
    setItemSectionsExpanded({ base: false, modifiers: false, ingredients: false });
    setClonedItemImageUrl('');
    setClonedItemImageName('');
  };

  const applyItemTemplate = (itemId) => {
    const sourceItem = menuItems.find((item) => String(item.id) === String(itemId));
    if (!sourceItem) return;
    const targetName = formData.name;
    applyItemFormState(sourceItem, { editing: false });
    // Name is entered before Clone As; cloning copies configuration, not the new identity.
    setFormData((current) => ({ ...current, name: targetName }));
    setItemCloneSourceId(String(itemId));
    setItemImage(null);
    setClonedItemImageUrl(sourceItem.image || '');
    setClonedItemImageName(sourceItem.image ? `${sourceItem.name} image` : '');
    toast.success(`Cloned from ${sourceItem.name}`);
  };

  const openEditModal = (item) => {
    applyItemFormState(item, { editing: true });
    setItemCloneSourceId('');
    setShowModal(true);
  };

  const openRecipeModal = (item) => {
    setSelectedItem(item);
    setIngredients(item.recipe_ingredients?.map(i => ({
      raw_material_id: i.raw_material,
      quantity: i.quantity,
      name: i.raw_material_name
    })) || []);
    setShowRecipeModal(true);
  };

  const saveRecipe = async () => {
    try {
      await api.post(`/menu-items/${selectedItem.id}/update_recipe/`, { ingredients });
      toast.success('Recipe updated');
      setShowRecipeModal(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update recipe'));
    }
  };

  const addIngredient = () => {
    setCurrentIngredients([...currentIngredients, { raw_material_id: '', quantity: 0 }]);
  };

  const removeIngredient = (index) => {
    setCurrentIngredients(currentIngredients.filter((_, i) => i !== index));
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...currentIngredients];
    updated[index][field] = field === 'quantity' ? parseFloat(value) || 0 : value;
    if (field === 'raw_material_id') {
      const mat = rawMaterials.find(m => m.id === parseInt(value));
      updated[index].name = mat?.name || '';
    }
    setCurrentIngredients(updated);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const catData = { ...categoryForm };
      if (!catData.department) catData.department = null;
      let res;
      if (editingCategory) {
        res = await api.patch(`/categories/${editingCategory.id}/`, catData);
        toast.success('Category updated');
      } else {
        if (!catData.department) delete catData.department;
        res = await api.post('/categories/', catData);
        toast.success('Category added');
      }
      const catId = res.data.id || editingCategory?.id;
      if (catImage && catId) {
        await uploadImage('category', catId, catImage);
      }
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '', department: '' });
      setEditingCategory(null);
      setCatImage(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save category'));
    }
  };

  const handleDropCategory = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const next = [...categories];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setCategories(next);
    setDragIdx(null);
  };

  const handleSaveCategoryOrder = async () => {
    try {
      await api.post('/categories/reorder/', categories.map((c, i) => ({ id: c.id, pos_order: i })));
      toast.success('Category order saved');
      setShowCategoryOrderModal(false);
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? All items in it will be unassigned.')) return;
    try {
      await api.delete(`/categories/${id}/`);
      toast.success('Category deleted');
      if (selectedCategory === id) setSelectedCategory(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete category'));
    }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, description: cat.description || '', department: cat.department || '' });
    setCatImage(null);
    setShowCategoryModal(true);
  };

  const handleDeptSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDept) {
        await api.patch(`/departments/${editingDept.id}/`, deptForm);
        toast.success('Department updated');
      } else {
        await api.post('/departments/', deptForm);
        toast.success('Department added');
      }
      setShowDeptModal(false);
      setDeptForm({ name: '', description: '' });
      setEditingDept(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save department'));
    }
  };

  const deleteDept = async (id) => {
    if (!window.confirm('Delete this department? Categories will be unassigned.')) return;
    try {
      await api.delete(`/departments/${id}/`);
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete department'));
    }
  };

  const openAddServing = () => {
    setEditingServing(null);
    setServingForm({ name: '', description: '', is_active: true, sort_order: 0 });
    setServingOptions([{ name: '', description: '', is_active: true, sort_order: 0 }]);
    setShowServingModal(true);
  };

  const openEditServing = (serving) => {
    setEditingServing(serving);
    setServingForm({
      name: serving.name || '',
      description: serving.description || '',
      is_active: serving.is_active !== false,
      sort_order: serving.sort_order || 0,
    });
    setServingOptions((serving.options || []).length
      ? serving.options.map(option => ({
          id: option.id,
          name: option.name || '',
          description: option.description || '',
          is_active: option.is_active !== false,
          sort_order: option.sort_order || 0,
        }))
      : [{ name: '', description: '', is_active: true, sort_order: 0 }]);
    setShowServingModal(true);
  };

  const saveServing = async (e) => {
    e.preventDefault();
    try {
      let servingId = editingServing?.id;
      if (editingServing) {
        await api.patch(`/serving-types/${servingId}/`, servingForm);
      } else {
        const res = await api.post('/serving-types/', servingForm);
        servingId = res.data.id;
      }

      const existingOptions = editingServing?.options || [];
      await Promise.all(existingOptions.map(option => api.delete(`/serving-size-options/${option.id}/`)));
      await Promise.all(servingOptions
        .filter(option => option.name.trim())
        .map((option, index) => api.post('/serving-size-options/', {
          serving_type: servingId,
          name: option.name.trim(),
          description: option.description || '',
          is_active: option.is_active !== false,
          sort_order: option.sort_order || index,
        })));

      toast.success(editingServing ? 'Serving size updated' : 'Serving size added');
      setShowServingModal(false);
      setEditingServing(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save serving size'));
    }
  };

  const deleteServing = async (serving) => {
    if (!window.confirm(`Delete serving "${serving.name}"?`)) return;
    try {
      await api.delete(`/serving-types/${serving.id}/`);
      toast.success('Serving size deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete serving size'));
    }
  };

  const updateServingOption = (index, field, value) => {
    setServingOptions(prev => prev.map((option, optionIndex) => (
      optionIndex === index ? { ...option, [field]: value } : option
    )));
  };

  const openPriceHistory = async (item) => {
    try {
      const res = await api.get(`/menu-items/${item.id}/price_history/`);
      setSelectedPriceHistoryItem(item);
      setPriceHistoryData({ prices: res.data.prices || [], costs: res.data.costs || [] });
      setExpandedPriceHistoryRows(new Set());
      setShowPriceHistoryModal(true);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load price history'));
    }
  };

  const togglePriceHistoryRow = (rowId) => {
    setExpandedPriceHistoryRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const submitHistoryEntry = async () => {
    if (!historyEntryDate || !historyEntryValue) { toast.error('Date and value required'); return; }
    setHistoryEntrySubmitting(true);
    try {
      await api.post(`/menu-items/${selectedPriceHistoryItem.id}/add_price_history/`, {
        type: historyEntryType,
        date: historyEntryDate,
        time: historyEntryTime || '12:00',
        value: parseFloat(historyEntryValue),
      });
      toast.success('History entry added');
      setShowAddHistoryForm(false);
      setHistoryEntryDate('');
      setHistoryEntryTime('12:00');
      setHistoryEntryValue('');
      openPriceHistory(selectedPriceHistoryItem);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to add history'));
    } finally {
      setHistoryEntrySubmitting(false);
    }
  };

  const openBulkPriceHistory = () => {
    const entries = menuItems
      .filter(mi => mi.is_active !== false)
      .map(mi => ({ menu_item_id: mi.id, name: mi.name, current_price: mi.selling_price, price: mi.selling_price }));
    setBulkHistoryEntries(entries);
    setBulkHistoryModified(new Set());
    setBulkHistoryDate('');
    setBulkHistoryTime('12:00');
    setShowBulkPriceHistory(true);
  };

  const updateBulkEntry = (index, price) => {
    setBulkHistoryEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], price };
      return updated;
    });
    setBulkHistoryModified(prev => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const submitBulkPriceHistory = async () => {
    if (!bulkHistoryDate) { toast.error('Select a date'); return; }
    const entries = bulkHistoryEntries
      .filter((e, i) => e.price !== '' && e.price !== null && e.price !== undefined)
      .map(e => ({ menu_item_id: e.menu_item_id, price: parseFloat(e.price) }));
    if (!entries.length) { toast.error('No entries to save'); return; }
    setBulkHistorySubmitting(true);
    try {
      const res = await api.post('/menu-items/bulk_add_price_history/', {
        date: bulkHistoryDate,
        time: bulkHistoryTime || '12:00',
        entries,
      });
      toast.success(`${res.data.created} price entries added`);
      setShowBulkPriceHistory(false);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save'));
    } finally {
      setBulkHistorySubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedItem(null);
    setFormData({ name: '', category: '', serving_type: '', selling_price: 0, description: '', is_available: true });
    setIngredients([]);
    setBaseComponents([]);
    setPortionConfigs([]);
    setSelectedPortionOptionId('');
    setSelectedModifiers([]);
    setItemImage(null);
    setClonedItemImageUrl('');
    setClonedItemImageName('');
    setItemCloneSourceId('');
    setItemSectionsExpanded({ base: false, modifiers: false, ingredients: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const pageTabs = [
    { id: 'items', label: 'Items' },
    { id: 'modifiers', label: 'Modifier' },
    { id: 'components', label: 'Component' },
    { id: 'setup', label: 'Setup' },
  ];

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const q = query.trim().toLowerCase();
      const results = [];
      const pushIfMatch = (type, name, details, record, extraSearch = []) => {
        const haystack = [name, details, ...extraSearch].filter(Boolean).join(' ').toLowerCase();
        if (haystack.includes(q)) {
          results.push({ type, name, details, record });
        }
      };

      menuItems.forEach(item => {
        pushIfMatch('item', item.name, item.category_name || 'Menu Item', item, [item.description]);
      });
      recipeComponents.forEach(component => {
        pushIfMatch('component', component.name, 'Component', component, [component.description]);
      });
      modifiers.forEach(modifier => {
        const modifierName = modifier.display_name || modifier.name;
        pushIfMatch('modifier', modifierName, `${(modifier.options || []).length} options`, modifier, [modifier.name, modifier.description]);
        (modifier.options || []).forEach(option => {
          pushIfMatch(
            'modifier_option',
            option.name,
            `Option in ${modifierName}`,
            { ...option, modifierId: modifier.id, modifierName },
            [option.description, option.raw_material_name, modifierName]
          );
        });
      });
      departments.forEach(department => {
        pushIfMatch('department', department.name, 'Setup / Department', department, [department.description]);
      });
      categories.forEach(category => {
        pushIfMatch('category', category.name, `Setup / Category${category.department_name ? ` / ${category.department_name}` : ''}`, category, [category.description]);
      });
      servingTypes.forEach(serving => {
        pushIfMatch('serving', serving.name, 'Setup / Serving Size', serving, [serving.description]);
        (serving.options || []).forEach(option => {
          pushIfMatch(
            'serving_option',
            option.name,
            `Serving option in ${serving.name}`,
            { ...option, servingId: serving.id },
            [option.description, serving.name]
          );
        });
      });

      setSearchResults(results.sort((a, b) => a.name.localeCompare(b.name) || a.type.localeCompare(b.type)));
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchItemClick = (item) => {
    if (item.type === 'item') {
      setPageTab('items');
      setSelectedCategory(null);
      clearSearch();
      openEditModal(item.record);
    } else if (item.type === 'component') {
      setPageTab('components');
      setCompositionEditTarget({ type: 'components', id: item.record.id });
      clearSearch();
    } else if (item.type === 'modifier') {
      setPageTab('modifiers');
      setCompositionEditTarget({ type: 'modifiers', id: item.record.id });
      clearSearch();
    } else if (item.type === 'modifier_option') {
      setPageTab('modifiers');
      setCompositionEditTarget({ type: 'modifiers', id: item.record.modifierId });
      clearSearch();
    } else if (item.type === 'department') {
      setPageTab('setup');
      setSetupTab('departments');
      setEditingDept(item.record);
      setDeptForm({ name: item.record.name || '', description: item.record.description || '' });
      setShowDeptModal(true);
      clearSearch();
    } else if (item.type === 'category') {
      setPageTab('setup');
      setSetupTab('categories');
      openEditCategory(item.record);
      clearSearch();
    } else if (item.type === 'serving' || item.type === 'serving_option') {
      const servingRecord = item.type === 'serving'
        ? item.record
        : servingTypes.find(serving => String(serving.id) === String(item.record.servingId));
      if (!servingRecord) return;
      setPageTab('setup');
      setSetupTab('servings');
      openEditServing(servingRecord);
      clearSearch();
    }
  };

  const currentSummaryCards = pageTab === 'setup'
    ? menuPageSummary.setup
    : pageTab === 'items'
      ? menuPageSummary.items
      : pageTab === 'modifiers'
        ? menuPageSummary.modifiers
        : menuPageSummary.components;

  const renderSearchPanel = () => (
    <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
      <UiSearchField
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Search items, components, modifiers, options, setup..."
      />
      {searchQuery ? (
        <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-800">Found Records</h3>
            <div className="flex items-center gap-3">
              {searchLoading ? <span className="text-xs text-gray-400">Searching...</span> : null}
              <span className="text-xs text-gray-500">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          {searchLoading ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">Searching...</div>
          ) : sortedSearchResults.length > 0 ? (
            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="bg-white sticky top-0 border-b border-gray-100">
                  <tr>
                    <SortableHeader label="Type" col="type" sortKey={searchSortKey} sortDir={searchSortDir} onSort={handleSearchSort} />
                    <SortableHeader label="Name" col="name" sortKey={searchSortKey} sortDir={searchSortDir} onSort={handleSearchSort} />
                    <SortableHeader label="Details" col="details" sortKey={searchSortKey} sortDir={searchSortDir} onSort={handleSearchSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedSearchResults.map((result, index) => (
                    <tr
                      key={`${result.type}-${result.record?.id || result.name}-${index}`}
                      onClick={() => handleSearchItemClick(result)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-primary-700">{result.type.replace('_', ' ')}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{result.name}</td>
                      <td className="px-4 py-3 text-gray-600">{result.details || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-6">No results found</p>
          )}
        </div>
      ) : null}
    </div>
  );

  const renderSummaryPanel = () => (
    <UiSummaryCards cols={`grid-cols-2 ${currentSummaryCards.length > 4 ? 'xl:grid-cols-5' : 'lg:grid-cols-4'}`}>
      {currentSummaryCards.map((card) => (
        <UiSummaryCard key={card.label} label={card.label} value={card.value} tone={card.tone} />
      ))}
    </UiSummaryCards>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Menu Management</h1>
          <p className="text-sm sm:text-base text-gray-500">Manage reusable menu data, item pricing, recipes, components, and modifiers</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showUnavailableMenuItems}
              onChange={(e) => setShowUnavailableMenuItems(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Show unavailable menu items
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showUnavailableModifierItems}
              onChange={(e) => setShowUnavailableModifierItems(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Show unavailable modifier items
          </label>
        </div>
      </div>

      {renderSearchPanel()}

      {/* Page-level Tabs */}
      <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1 overflow-x-auto">
        <UiTabs tabs={pageTabs} active={pageTab} onChange={setPageTab} />
      </div>

      {pageTab === 'setup' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <UiTabs
            size="sm"
            tabs={[
              { id: 'servings', label: 'Servings' },
              { id: 'categories', label: 'Category' },
              { id: 'departments', label: 'Department' },
            ]}
            active={setupTab}
            onChange={setSetupTab}
          />

          {setupTab === 'departments' && (
            <div className="space-y-3">
              <div className="flex justify-end">
                {hasPermission('menu', 'add') && (
                  <button onClick={() => { setEditingDept(null); setDeptForm({ name: '', description: '' }); setShowDeptModal(true); }} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
                    Add Department
                  </button>
                )}
              </div>
              {renderSummaryPanel()}
              {departments.map((dept) => (
                <div key={dept.id} className="border rounded-xl p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-800">{dept.name}</p>
                    <p className="text-sm text-gray-500">{dept.category_count || 0} categories{dept.description ? ` - ${dept.description}` : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    {hasPermission('menu', 'edit') && (
                      <button onClick={() => { setEditingDept(dept); setDeptForm({ name: dept.name, description: dept.description || '' }); setShowDeptModal(true); }} className="p-2 border rounded-lg text-blue-600 hover:bg-blue-50">
                        <Edit2 size={16} />
                      </button>
                    )}
                    {hasPermission('menu', 'delete') && (
                      <button onClick={() => deleteDept(dept.id)} className="p-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {departments.length === 0 && <div className="text-center py-8 text-gray-500 border rounded-xl">No departments added</div>}
            </div>
          )}
          {setupTab === 'categories' && (
            <div className="space-y-3">
              <div className="flex flex-wrap justify-end gap-2">
                {hasPermission('menu', 'add') && (
                  <button onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', description: '', department: '' }); setCatImage(null); setShowCategoryModal(true); }} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
                    Add Category
                  </button>
                )}
                {hasPermission('menu', 'edit') && (
                  <button onClick={() => setShowCategoryOrderModal(true)} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
                    Sort Category
                  </button>
                )}
              </div>
              {renderSummaryPanel()}
              {categories.map((cat) => (
                <div key={cat.id} className="border rounded-xl p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {cat.image ? <img src={cat.image} alt="" className="w-10 h-10 rounded object-cover" /> : <ChefHat size={20} className="text-primary-500" />}
                    <div>
                      <p className="font-medium text-gray-800">{cat.name}</p>
                      <p className="text-sm text-gray-500">{cat.department_name || 'Store-wide'}{typeof cat.item_count === 'number' ? ` - ${cat.item_count} items` : ''}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {hasPermission('menu', 'edit') && (
                      <button onClick={() => openEditCategory(cat)} className="p-2 border rounded-lg text-blue-600 hover:bg-blue-50">
                        <Edit2 size={16} />
                      </button>
                    )}
                    {hasPermission('menu', 'delete') && (
                      <button onClick={() => deleteCategory(cat.id)} className="p-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {categories.length === 0 && <div className="text-center py-8 text-gray-500 border rounded-xl">No categories added</div>}
            </div>
          )}

          {setupTab === 'servings' && (
            <div className="space-y-3">
              <div className="flex justify-end">
                {hasPermission('menu', 'add') && (
                  <button onClick={openAddServing} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
                    Add Serving
                  </button>
                )}
              </div>
              {renderSummaryPanel()}
              {servingTypes.map((serving) => (
                <div key={serving.id} className="border rounded-xl p-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-800">{serving.name}</p>
                    {serving.description && <p className="text-sm text-gray-500">{serving.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(serving.options || []).length > 0
                        ? serving.options.map(option => <span key={option.id} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">{option.name}</span>)
                        : <span className="text-sm text-gray-400">No portions added</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {hasPermission('menu', 'edit') && (
                      <button onClick={() => openEditServing(serving)} className="p-2 border rounded-lg text-blue-600 hover:bg-blue-50">
                        <Edit2 size={16} />
                      </button>
                    )}
                    {hasPermission('menu', 'delete') && (
                      <button onClick={() => deleteServing(serving)} className="p-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {servingTypes.length === 0 && <div className="text-center py-8 text-gray-500 border rounded-xl">No serving sizes added</div>}
            </div>
          )}
        </div>
      )}

      {pageTab === 'cat_order' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">POS Category Order</h3>
              <p className="text-sm text-gray-500">Drag to reorder — categories appear in this order on the POS screen</p>
            </div>
            {hasPermission('menu', 'edit') && (
              <button onClick={handleSaveCategoryOrder} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700 flex items-center gap-2">
                Save Order
              </button>
            )}
          </div>
          <div className="space-y-2">
            {categories.map((cat, idx) => {
              const CatIcon = getCategoryIcon(cat.name);
              return (
                <div
                  key={cat.id}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDropCategory(idx)}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex items-center gap-3 p-3 border rounded-lg select-none ${
                    dragIdx === idx ? 'opacity-40 border-dashed border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50 cursor-grab'
                  }`}
                >
                  <GripVertical size={18} className="text-gray-400 flex-shrink-0" />
                  <span className="text-gray-400 text-sm w-7 flex-shrink-0">#{idx + 1}</span>
                  {cat.image
                    ? <img src={cat.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                    : <CatIcon size={20} className="text-primary-500 flex-shrink-0" />}
                  <span className="font-medium text-gray-800">{cat.name}</span>
                  {cat.department_name && <span className="text-xs text-gray-400">({cat.department_name})</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pageTab === 'modifiers' && (
        <>
          <ModifierManager
            key="modifiers"
            defaultTab="modifiers"
            hideTabs={true}
            useModalForms={true}
            openEditId={compositionEditTarget?.type === 'modifiers' ? compositionEditTarget.id : null}
            onOpenEditHandled={() => setCompositionEditTarget(null)}
            onDataChanged={fetchData}
            showUnavailableModifierItems={showUnavailableModifierItems}
            summaryContent={renderSummaryPanel()}
          />
        </>
      )}
      {pageTab === 'components' && (
        <>
          <ModifierManager
            key="components"
            defaultTab="components"
            hideTabs={true}
            useModalForms={true}
            openEditId={compositionEditTarget?.type === 'components' ? compositionEditTarget.id : null}
            onOpenEditHandled={() => setCompositionEditTarget(null)}
            onDataChanged={fetchData}
            summaryContent={renderSummaryPanel()}
          />
        </>
      )}

      {pageTab === 'items' && (
        <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {hasPermission('menu', 'edit') && (
            <button onClick={openBulkPriceHistory} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              Bulk Price History
            </button>
          )}
          {hasPermission('menu', 'add') && (
            <button onClick={() => { resetForm(); setShowModal(true); }} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              Add Item
            </button>
          )}
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setSelectedCategory(null)} className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm flex-shrink-0 ${!selectedCategory ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>All</button>
          {categories.map((cat) => (
            <div key={cat.id} className="relative group flex items-center flex-shrink-0">
              <button onClick={() => setSelectedCategory(cat.id)} className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-1.5 ${selectedCategory === cat.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {cat.image && <img src={cat.image} alt="" className="w-7 h-7 rounded object-cover" />}
                {cat.name}
                {cat.department_name && <span className="text-xs opacity-70">({cat.department_name})</span>}
              </button>
              {hasPermission('menu', 'edit') && (
                <div className="hidden group-hover:flex absolute -top-1 -right-1 gap-0.5 z-10">
                  <button onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }} className="p-1 bg-blue-500 text-white rounded-full shadow hover:bg-blue-600" title="Edit category">
                    <Edit2 size={10} />
                  </button>
                  {hasPermission('menu', 'delete') && (
                    <button onClick={(e) => { e.stopPropagation(); deleteCategory(cat.id); }} className="p-1 bg-red-500 text-white rounded-full shadow hover:bg-red-600" title="Delete category">
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {renderSummaryPanel()}

      {/* Menu Items Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {filteredItems.map((item) => (
          <div key={item.id} className={`bg-white rounded-xl shadow-sm overflow-hidden border ${!item.is_available ? 'opacity-60' : ''}`}>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 text-center">
              {item.image ? (
                <img src={item.image} alt={item.name} className="w-20 h-20 mx-auto mb-2 rounded-lg object-cover" />
              ) : (
                (() => { const CatIcon = getCategoryIcon(item.category_name); return <CatIcon size={40} className="mx-auto mb-2 text-primary-500" />; })()
              )}
              <h3 className="font-bold text-gray-800">{item.name}</h3>
              <p className="text-sm text-gray-500">{item.category_name}</p>
            </div>
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                {(item.portion_configs?.length || 0) > 1 ? (
                  <span className="text-xl font-bold text-primary-600">
                    {[...item.portion_configs].sort((a, b) => parseFloat(a.price) - parseFloat(b.price)).map(p => formatUiCurrencyCompact(p.price)).join(' | ')}
                  </span>
                ) : (
                  <span className="text-xl font-bold text-primary-600">{formatUiCurrencyCompact(item.selling_price)}</span>
                )}
                <span className={`px-2 py-1 text-xs rounded-full ${item.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {item.is_available ? 'Available' : 'Unavailable'}
                </span>
              </div>
              {/* Subscript — servings, ingredients, base, modifiers */}
              {(() => {
                const hasPortions = (item.portion_configs?.length || 0) > 0;
                // For portion items, recipe lives per-portion; show the representative count (max across portions)
                const ingCount = hasPortions
                  ? Math.max(0, ...item.portion_configs.map(p => p.recipe_ingredients?.length || 0))
                  : (item.recipe_ingredients?.length || 0);
                const baseCount = hasPortions
                  ? Math.max(0, ...item.portion_configs.map(p => p.base_components?.length || 0))
                  : (item.base_components?.length || 0);
                const modCount = item.modifiers?.length || 0;
                return (
                  <div className="text-xs text-gray-400 mb-3 space-y-0.5">
                    {hasPortions && (
                      <div>Servings: {item.serving_type_detail?.name || 'Portions'} ({[...item.portion_configs].sort((a, b) => parseFloat(a.price) - parseFloat(b.price)).map(p => p.serving_size_option_name || '?').join(', ')})</div>
                    )}
                    {ingCount > 0 && <div>Ingredients: {ingCount}</div>}
                    {baseCount > 0 && <div>Base: {baseCount}</div>}
                    {modCount > 0 && <div>Modifiers: {modCount}</div>}
                  </div>
                );
              })()}
              <div className="flex gap-2">
                {hasPermission('menu', 'edit') && (
                  <>
                    <button onClick={() => toggleFavorite(item)} className={`p-2 border rounded-lg ${item.is_favorite ? 'bg-yellow-50 border-yellow-300 text-yellow-600' : 'border-gray-200 hover:bg-yellow-50 text-gray-400'}`} title={item.is_favorite ? 'Remove from favorites' : 'Add to favorites'}>
                      <Star size={16} fill={item.is_favorite ? 'currentColor' : 'none'} />
                    </button>
                    <button onClick={() => { setCostModalItem(item); setShowCostModal(true); }} className="p-2 border border-gray-200 rounded-lg hover:bg-green-50 text-green-700 font-bold text-xs" title="View cost breakdown">
                      $
                    </button>
                    <button onClick={() => openPriceHistory(item)} className="p-2 border border-gray-200 rounded-lg hover:bg-primary-50 text-primary-600" title="Price and cost history">
                      <History size={16} />
                    </button>
                    <button onClick={() => toggleAvailability(item)} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                      {item.is_available ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button onClick={() => openEditModal(item)} className="p-2 border border-gray-200 rounded-lg hover:bg-blue-50 text-blue-600">
                      <Edit2 size={16} />
                    </button>
                  </>
                )}
                {hasPermission('menu', 'delete') && (
                  <button onClick={() => handleDelete(item.id)} className="p-2 border border-gray-200 rounded-lg hover:bg-red-50 text-red-600">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl">No menu items found</div>
      )}
        </>
      )}

      {showCategoryOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">Category Order</h3>
              <button onClick={() => setShowCategoryOrderModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-2 mb-6">
              {categories.map((cat, idx) => {
                const CatIcon = getCategoryIcon(cat.name);
                return (
                  <div
                    key={cat.id}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropCategory(idx)}
                    onDragEnd={() => setDragIdx(null)}
                    className={`flex items-center gap-3 p-3 border rounded-lg select-none ${
                      dragIdx === idx ? 'opacity-40 border-dashed border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50 cursor-grab'
                    }`}
                  >
                    <GripVertical size={18} className="text-gray-400 flex-shrink-0" />
                    <span className="text-gray-400 text-sm w-7 flex-shrink-0">#{idx + 1}</span>
                    {cat.image
                      ? <img src={cat.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                      : <CatIcon size={20} className="text-primary-500 flex-shrink-0" />}
                    <span className="font-medium text-gray-800">{cat.name}</span>
                    {cat.department_name && <span className="text-xs text-gray-400">({cat.department_name})</span>}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowCategoryOrderModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              {hasPermission('menu', 'edit') && (
                <button type="button" onClick={handleSaveCategoryOrder} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2">
                  Save Order
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Breakdown Popup */}
      {showCostModal && costModalItem && (() => {
        const item = costModalItem;
        const portions = item.portion_configs || [];
        const hasPortions = portions.length > 0;
        const fmt = (v) => formatUiCurrencyDetailed(v || 0);

        // Group modifier options by price_adjustment
        const modifierGroups = {};
        (item.modifiers || []).forEach(link => {
          (link.modifier_detail?.options || link.options || []).forEach(opt => {
            const adj = parseFloat(opt.price_adjustment || 0);
            const key = adj.toFixed(2);
            if (!modifierGroups[key]) modifierGroups[key] = { adj, names: [] };
            modifierGroups[key].names.push(opt.name || opt.modifier_option_name || '?');
          });
        });
        const modifierLines = Object.values(modifierGroups).sort((a, b) => b.adj - a.adj);

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-auto shadow-xl">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
                <h3 className="font-bold text-gray-800">{item.name} — Cost Breakdown</h3>
                <button onClick={() => setShowCostModal(false)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-4">
                {hasPortions ? (
                  portions.map(p => (
                    <div key={p.id} className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-800">{p.serving_size_option_name || 'Portion'}</h4>
                        <span className="text-sm font-bold text-primary-600">{fmt(p.price)}</span>
                      </div>
                      {(p.base_components?.length > 0) && (
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Base Components</p>
                          {(p.base_components || []).map((bc, i) => (
                            <div key={i} className="flex justify-between text-sm text-gray-700">
                              <span>{bc.recipe_component_name || 'Component'}</span>
                              <span className="text-gray-500">× {bc.quantity}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {(p.recipe_ingredients?.length > 0) && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Ingredients</p>
                          {(p.recipe_ingredients || []).map((ing, i) => (
                            <div key={i} className="flex justify-between text-sm text-gray-700">
                              <span>{ing.raw_material_name || 'Ingredient'}</span>
                              <span className="text-gray-500">× {ing.quantity}{ing.unit_name ? ` ${ing.unit_name}` : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="border-t mt-2 pt-2 flex justify-between text-sm font-semibold">
                        <span>Total Cost</span>
                        <span className="text-green-700">{fmt(p.runtime_cost_price ?? p.cost_price ?? item.runtime_cost_price)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="border border-gray-100 rounded-xl p-4">
                    {(item.base_components?.length > 0) && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Base Components</p>
                        {(item.base_components || []).map((bc, i) => (
                          <div key={i} className="flex justify-between text-sm text-gray-700">
                            <span>{bc.recipe_component_name || 'Component'}</span>
                            <span className="text-gray-500">× {bc.quantity}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(item.recipe_ingredients?.length > 0) && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Ingredients</p>
                        {(item.recipe_ingredients || []).map((ing, i) => (
                          <div key={i} className="flex justify-between text-sm text-gray-700">
                            <span>{ing.raw_material_name || 'Ingredient'}</span>
                            <span className="text-gray-500">× {ing.quantity}{ing.unit_name ? ` ${ing.unit_name}` : ''}{ing.ingredient_cost != null ? ` · ${fmt(ing.ingredient_cost)}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold border-t pt-2">
                      <span>Total Cost</span>
                      <span className="text-green-700">{fmt(item.runtime_cost_price ?? item.cost_price)}</span>
                    </div>
                  </div>
                )}

                {/* Modifier price groups */}
                {modifierLines.length > 0 && (
                  <div className="border border-gray-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Modifier Price Impact</p>
                    {modifierLines.map((mg, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm mb-1">
                        <span className={`font-semibold flex-shrink-0 w-16 ${mg.adj > 0 ? 'text-red-600' : mg.adj < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {mg.adj > 0 ? '+' : ''}{fmt(mg.adj)}
                        </span>
                        <span className="text-gray-600">{mg.names.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {showPriceHistoryModal && selectedPriceHistoryItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">{selectedPriceHistoryItem.name} History</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddHistoryForm(!showAddHistoryForm)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition"
                >Add</button>
                <button onClick={() => setShowPriceHistoryModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
              </div>
            </div>

            {showAddHistoryForm && (
              <div className="mb-4 p-4 bg-gray-50 rounded-xl border space-y-3">
                <div className="flex items-center gap-2">
                  {['price', 'cost'].map(t => (
                    <button key={t} onClick={() => setHistoryEntryType(t)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${historyEntryType === t ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border'}`}
                    >{t === 'price' ? 'Price' : 'Cost'}</button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Date</label>
                    <UiDateField value={historyEntryDate} onChange={setHistoryEntryDate} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Time</label>
                    <UiTimeField value={historyEntryTime} onChange={setHistoryEntryTime} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">{historyEntryType === 'price' ? 'Price' : 'Cost'}</label>
                    <input type="number" step="0.01" value={historyEntryValue} onChange={e => setHistoryEntryValue(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
                  </div>
                </div>
                <button onClick={submitHistoryEntry} disabled={historyEntrySubmitting}
                  className="w-full py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                >{historyEntrySubmitting ? 'Saving...' : 'Save Entry'}</button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 font-semibold text-gray-800">Price History</div>
                {sortedPriceHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-gray-100">
                        <tr>
                          <SortableHeader label="Date / Time" col="effective_from" sortKey={priceSortKey} sortDir={priceSortDir} onSort={handlePriceSort} />
                          <SortableHeader label="Price" col="price" sortKey={priceSortKey} sortDir={priceSortDir} onSort={handlePriceSort} right />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sortedPriceHistory.map((row) => {
                          const modifierOptionPrices = row.modifier_option_prices || [];
                          const isExpanded = expandedPriceHistoryRows.has(row.id);
                          return (
                            <React.Fragment key={row.id}>
                              <tr className={modifierOptionPrices.length > 0 ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => modifierOptionPrices.length > 0 && togglePriceHistoryRow(row.id)}>
                                <td className="px-4 py-3 text-gray-600">
                                  <div className="flex items-center gap-2">
                                    {modifierOptionPrices.length > 0 ? (
                                      isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />
                                    ) : <span className="w-[14px] inline-block" />}
                                    <span>{formatUiDateTime(row.effective_from)}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-primary-600">{formatUiCurrencyDetailed(row.price || 0)}</td>
                              </tr>
                              {isExpanded && modifierOptionPrices.map((modifierRow) => (
                                <tr key={`${row.id}-${modifierRow.modifier_option_id}`} className="bg-gray-50/70">
                                  <td className="px-4 py-2 pl-10 text-gray-600">
                                    <div className="font-medium text-gray-700">{modifierRow.modifier_option_name}</div>
                                    <div className="text-xs text-gray-400">
                                      {modifierRow.modifier_group_name} · {parseFloat(modifierRow.price_adjustment || 0) >= 0 ? '+' : ''}{formatUiCurrencyDetailed(modifierRow.price_adjustment || 0)}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-right font-medium text-emerald-700">{formatUiCurrencyDetailed(modifierRow.total_price || 0)}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="px-4 py-6 text-sm text-gray-500 text-center">No price history</div>}
              </div>
              <div className="border rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 font-semibold text-gray-800">Cost History</div>
                {sortedCostHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-gray-100">
                        <tr>
                          <SortableHeader label="Date / Time" col="effective_from" sortKey={costSortKey} sortDir={costSortDir} onSort={handleCostSort} />
                          <SortableHeader label="Cost" col="cost_price" sortKey={costSortKey} sortDir={costSortDir} onSort={handleCostSort} right />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sortedCostHistory.map((row) => (
                          <tr key={row.id}>
                            <td className="px-4 py-3 text-gray-600">{formatUiDateTime(row.effective_from)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-red-600">{formatUiCurrencyDetailed(row.cost_price || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="px-4 py-6 text-sm text-gray-500 text-center">No cost history</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {showServingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">{editingServing ? 'Edit Serving' : 'Add Serving'}</h3>
              <button onClick={() => setShowServingModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <form onSubmit={saveServing} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serving Name *</label>
                  <input required value={servingForm.name} onChange={(e) => setServingForm({ ...servingForm, name: e.target.value })} placeholder="Glass, Bowl, Plate" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                  <input type="number" value={servingForm.sort_order} onChange={(e) => setServingForm({ ...servingForm, sort_order: parseInt(e.target.value || 0, 10) })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input value={servingForm.description} onChange={(e) => setServingForm({ ...servingForm, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Portions</label>
                  <button type="button" onClick={() => setServingOptions([...servingOptions, { name: '', description: '', is_active: true, sort_order: servingOptions.length }])} className="text-primary-600 text-sm flex items-center gap-1">
                    Add Portion
                  </button>
                </div>
                {servingOptions.map((option, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_80px_40px] gap-2 items-center">
                    <input value={option.name} onChange={(e) => updateServingOption(index, 'name', e.target.value)} placeholder="Small, Medium, Large" className="px-3 py-2 border rounded-lg text-sm" />
                    <input value={option.description} onChange={(e) => updateServingOption(index, 'description', e.target.value)} placeholder="Optional description" className="px-3 py-2 border rounded-lg text-sm" />
                    <input type="number" value={option.sort_order} onChange={(e) => updateServingOption(index, 'sort_order', parseInt(e.target.value || 0, 10))} className="px-3 py-2 border rounded-lg text-sm" />
                    <button type="button" onClick={() => setServingOptions(servingOptions.length === 1 ? [{ name: '', description: '', is_active: true, sort_order: 0 }] : servingOptions.filter((_, i) => i !== index))} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-4">
                <button type="button" onClick={() => setShowServingModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save Serving</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">{selectedItem ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <VoiceInput type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter item name" />
                </div>
                {!selectedItem && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Clone As</label>
                    <select
                      value={itemCloneSourceId}
                      onChange={(e) => {
                        const sourceId = e.target.value;
                        if (!sourceId) {
                          const targetName = formData.name;
                          resetForm();
                          setFormData((current) => ({ ...current, name: targetName }));
                          return;
                        }
                        applyItemTemplate(sourceId);
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Start blank</option>
                      {menuItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {formatCloneOptionLabel(item)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select required value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="">Select Category</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}{c.department_name ? ` (${c.department_name})` : ''}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Image</label>
                <div className="flex items-center gap-2">
                  {(selectedItem?.image || itemImage || clonedItemImageUrl) && (
                    <img src={itemImage ? URL.createObjectURL(itemImage) : (clonedItemImageUrl || selectedItem?.image)} alt="" className="w-10 h-10 rounded-lg object-cover border" />
                  )}
                  <label className="flex-1 flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-500">
                    {itemImage ? itemImage.name : (clonedItemImageName || 'Upload image')}
                    <input type="file" accept="image/*" onChange={(e) => {
                      const nextFile = e.target.files?.[0] || null;
                      setItemImage(nextFile);
                      if (nextFile) {
                        setClonedItemImageUrl('');
                        setClonedItemImageName('');
                      }
                    }} className="hidden" />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <VoiceInput isTextarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description" rows={2} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serving Size</label>
                  <select value={formData.serving_type} onChange={(e) => setFormData({ ...formData, serving_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                    <option value="">No serving size</option>
                    {servingTypes.map((serving) => (
                      <option key={serving.id} value={serving.id}>{serving.name}</option>
                    ))}
                  </select>
                </div>
                {formData.serving_type ? (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Portions</label>
                    {servingTypeOptions.length === 0 ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                        No portions are defined for this serving size yet.
                      </div>
                    ) : (
                      <>
                        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                          Every portion shown here will appear in POS. Fill each tab separately so each portion keeps its own price, base components, ingredients, and availability.
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {servingTypeOptions.map((option) => {
                            const config = portionConfigs.find((entry) => String(entry.serving_size_option_id) === String(option.id));
                            const status = getPortionConfigStatus(config);
                            const active = String(selectedPortionOptionId) === String(option.id);
                            return (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => setSelectedPortionOptionId(String(option.id))}
                                className={`rounded-lg border px-3 py-2 text-sm transition ${
                                  active
                                    ? 'border-primary-500 bg-primary-600 text-white'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <span className="font-medium">{option.name}</span>
                                <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  active
                                    ? 'bg-white/20 text-white'
                                    : status === 'configured'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {status === 'configured' ? 'Ready' : 'Needs price'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.serving_type && selectedPortionConfig
                    ? `Price for ${selectedPortionConfig.serving_size_option_name || 'selected portion'} *`
                    : 'Selling Price *'}
                </label>
                <VoiceInput
                  type="number"
                  step="0.01"
                  required
                  value={currentPrice}
                  onChange={(e) => setCurrentPrice(e.target.value)}
                  placeholder="0"
                  disabled={Boolean(formData.serving_type) && !selectedPortionConfig}
                />
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleItemSection('base')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {formData.serving_type && selectedPortionConfig
                      ? `Base Components for ${selectedPortionConfig.serving_size_option_name || 'selected portion'}`
                      : 'Base Components'}
                  </span>
                  {itemSectionsExpanded.base ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
                </button>
                {itemSectionsExpanded.base && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      <button type="button" onClick={() => setCurrentBaseComponents([...currentBaseComponents, { recipe_component_id: '', quantity: 1, sort_order: currentBaseComponents.length }])} className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                        Add Base Component
                      </button>
                    </div>
                    {currentBaseComponents.length === 0 ? (
                      <p className="text-sm text-gray-400">No base components added</p>
                    ) : (
                      <div className="space-y-2">
                        {currentBaseComponents.map((component, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <select value={component.recipe_component_id} onChange={(e) => {
                              const next = [...currentBaseComponents];
                              next[index].recipe_component_id = e.target.value;
                              setCurrentBaseComponents(next);
                            }} className="flex-1 px-3 py-2 border rounded-lg text-sm" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                              <option value="">Select Base Component</option>
                              {recipeComponents.map((recipeComponent) => (<option key={recipeComponent.id} value={recipeComponent.id}>{recipeComponent.name}</option>))}
                            </select>
                            <input type="number" step="0.001" min="0.001" placeholder="Qty." value={component.quantity} onChange={(e) => {
                              const next = [...currentBaseComponents];
                              next[index].quantity = parseFloat(e.target.value) || 0;
                              setCurrentBaseComponents(next);
                            }} className="w-24 px-3 py-2 border rounded-lg text-sm" disabled={Boolean(formData.serving_type) && !selectedPortionConfig} />
                            <button type="button" onClick={() => setCurrentBaseComponents(currentBaseComponents.filter((_, i) => i !== index))} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleItemSection('modifiers')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {formData.serving_type && selectedPortionConfig
                      ? `Modifiers for ${selectedPortionConfig.serving_size_option_name || 'selected portion'}`
                      : 'Modifiers'}
                  </span>
                  {itemSectionsExpanded.modifiers ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
                </button>
                {itemSectionsExpanded.modifiers && (
                  <div className="p-4 space-y-3">
                    {formData.serving_type && !selectedPortionConfig ? (
                      <p className="text-sm text-amber-600">Select a portion above to set its modifiers.</p>
                    ) : (
                    <>
                    <div className="flex justify-end">
                      <button type="button" onClick={addMenuModifierRow} className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                        Add Modifier
                      </button>
                    </div>
                    {currentModifiers.length === 0 ? (
                      <p className="text-sm text-gray-400">No modifiers added</p>
                    ) : (
                      <div className="space-y-3">
                        {currentModifiers.map((row, index) => {
                          const selectedGroup = modifiers.find((group) => String(group.id) === String(row.modifier_group_id));
                          return (
                            <div
                              key={`${row.modifier_group_id || 'modifier'}-${index}`}
                              ref={(element) => { menuModifierRowRefs.current[index] = element; }}
                              className="rounded-lg border border-gray-200 p-3 space-y-3"
                            >
                              <div className="flex gap-2 items-center">
                                <select
                                  value={row.modifier_group_id}
                                  onChange={(e) => updateMenuModifierRow(index, 'modifier_group_id', e.target.value)}
                                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                                >
                                  <option value="">Select Modifier</option>
                                  {modifiers
                                    .filter((group) => (
                                      String(group.id) === String(row.modifier_group_id)
                                      || visibleModifierGroups.some((visibleGroup) => String(visibleGroup.id) === String(group.id))
                                    ))
                                    .map((group) => (
                                      <option key={group.id} value={group.id}>{group.display_name || group.name}</option>
                                    ))}
                                </select>
                                <button type="button" onClick={() => removeMenuModifierRow(index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              {row.modifier_group_id && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                  <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={row.is_required} onChange={(e) => updateMenuModifierRow(index, 'is_required', e.target.checked)} />
                                    Required
                                  </label>
                                  <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={row.allow_multiple_quantities} onChange={(e) => updateMenuModifierRow(index, 'allow_multiple_quantities', e.target.checked)} />
                                    Allow Repeats
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs text-gray-500">Min picks</span>
                                    <input type="number" min="0" value={row.min_selections} onChange={(e) => updateMenuModifierRow(index, 'min_selections', parseInt(e.target.value || 0, 10) || 0)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                                  </label>
                                  <label className="flex flex-col gap-1">
                                    <span className="text-xs text-gray-500">Max picks</span>
                                    <input type="number" min="1" value={row.max_selections} onChange={(e) => updateMenuModifierRow(index, 'max_selections', parseInt(e.target.value || 1, 10) || 1)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                                  </label>
                                  {selectedGroup && (
                                    <div className="sm:col-span-2 text-xs text-gray-500">
                                      {(selectedGroup.options || []).length} option(s) defined for this modifier
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleItemSection('ingredients')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {formData.serving_type && selectedPortionConfig
                      ? `Recipe Ingredients for ${selectedPortionConfig.serving_size_option_name || 'selected portion'}`
                      : 'Recipe Ingredients'}
                  </span>
                  {itemSectionsExpanded.ingredients ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
                </button>
                {itemSectionsExpanded.ingredients && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      <button type="button" onClick={addIngredient} className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                        Add Ingredient
                      </button>
                    </div>
                    {currentIngredients.length === 0 ? (
                      <p className="text-sm text-gray-400">No ingredients added</p>
                    ) : (
                      <div className="space-y-2">
                        {currentIngredients.map((ing, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <select value={ing.raw_material_id} onChange={(e) => updateIngredient(index, 'raw_material_id', e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                              <option value="">Select Ingredient</option>
                              {rawMaterials.map((m) => (<option key={m.id} value={m.id}>{m.name} ({m.unit_abbr})</option>))}
                            </select>
                            <input type="number" step="0.001" min="0.001" placeholder="Qty." value={ing.quantity} onChange={(e) => updateIngredient(index, 'quantity', e.target.value)} className="w-24 px-3 py-2 border rounded-lg text-sm" disabled={Boolean(formData.serving_type) && !selectedPortionConfig} />
                            <button type="button" onClick={() => removeIngredient(index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" disabled={Boolean(formData.serving_type) && !selectedPortionConfig}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_available" checked={currentAvailability} onChange={(e) => setCurrentAvailability(e.target.checked)} className="w-4 h-4 text-primary-600 rounded" disabled={Boolean(formData.serving_type) && !selectedPortionConfig} />
                <label htmlFor="is_available" className="text-sm text-gray-700">
                  {formData.serving_type ? 'Available for sale for this portion' : 'Available for sale'}
                </label>
              </div>

              <div className="flex gap-2 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recipe Modal */}
      {showRecipeModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold">Recipe: {selectedItem.name}</h3>
              <button onClick={() => setShowRecipeModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <div className="space-y-3 mb-6">
              {ingredients.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No ingredients added</p>
              ) : (
                ingredients.map((ing, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span>{ing.name || rawMaterials.find(m => m.id === parseInt(ing.raw_material_id))?.name}</span>
                    <span className="font-medium">{ing.quantity} {rawMaterials.find(m => m.id === parseInt(ing.raw_material_id))?.unit_abbr}</span>
                  </div>
                ))
              )}
            </div>
            {hasPermission('menu', 'edit') && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Edit Recipe</span>
                  <button onClick={addIngredient} className="text-primary-600 text-sm flex items-center gap-1">Add</button>
                </div>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {ingredients.map((ing, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <select value={ing.raw_material_id} onChange={(e) => updateIngredient(index, 'raw_material_id', e.target.value)} className="flex-1 px-2 py-1 border rounded text-sm">
                        <option value="">Select</option>
                        {rawMaterials.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
                      </select>
                      <input type="number" step="0.001" min="0.001" value={ing.quantity} onChange={(e) => updateIngredient(index, 'quantity', e.target.value)} className="w-20 px-2 py-1 border rounded text-sm" />
                      <button onClick={() => removeIngredient(index)} className="text-red-500"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={saveRecipe} className="w-full mt-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save Recipe</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold">{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <button onClick={() => setShowCategoryModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <VoiceInput type="text" required value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="Enter category name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select value={categoryForm.department} onChange={(e) => setCategoryForm({ ...categoryForm, department: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="">Store-wide (No Department)</option>
                  {departments.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon/Image</label>
                <div className="flex items-center gap-2">
                  {(editingCategory?.image || catImage) && (
                    <img src={catImage ? URL.createObjectURL(catImage) : editingCategory.image} alt="" className="w-10 h-10 rounded-lg object-cover border" />
                  )}
                  <label className="flex-1 flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-500">
                    {catImage ? catImage.name : 'Upload category icon'}
                    <input type="file" accept="image/*" onChange={(e) => setCatImage(e.target.files[0])} className="hidden" />
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <VoiceInput isTextarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} placeholder="Enter description" rows={2} />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowCategoryModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-primary-600 text-white rounded-lg">{editingCategory ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Department Modal */}
      {showDeptModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold">{editingDept ? 'Edit Department' : 'Add Department'}</h3>
              <button onClick={() => setShowDeptModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>

            <form onSubmit={handleDeptSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department Name *</label>
                <input type="text" required value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} placeholder="e.g. Juices & Shakes" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} placeholder="Optional description" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex gap-2">
                {editingDept && (
                  <button type="button" onClick={() => { setEditingDept(null); setDeptForm({ name: '', description: '' }); }} className="flex-1 py-2 border border-gray-300 rounded-lg">Cancel Edit</button>
                )}
                <button type="submit" className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  {editingDept ? 'Update' : 'Add Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Bulk Price History Modal */}
      {showBulkPriceHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowBulkPriceHistory(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between shrink-0">
              <h3 className="text-lg font-bold text-gray-800">Bulk Historical Price Entry</h3>
              <button onClick={() => setShowBulkPriceHistory(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <div className="p-4 border-b space-y-3 shrink-0">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Date</label>
                  <UiDateField value={bulkHistoryDate} onChange={setBulkHistoryDate} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Time</label>
                  <UiTimeField value={bulkHistoryTime} onChange={setBulkHistoryTime} />
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Menu Item</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 w-32">Current</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 w-32">Price at Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bulkHistoryEntries.map((entry, idx) => (
                    <tr key={entry.menu_item_id} className={bulkHistoryModified.has(idx) ? 'bg-amber-50' : ''}>
                      <td className="px-3 py-2 font-medium text-gray-800">{entry.name}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{formatUiCurrencyCompact(entry.current_price)}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="0.01"
                          value={entry.price}
                          onChange={e => updateBulkEntry(idx, e.target.value)}
                          className={`w-full border rounded-lg px-2 py-1.5 text-sm text-right ${bulkHistoryModified.has(idx) ? 'border-amber-400 bg-amber-50 font-semibold text-amber-800' : 'border-gray-200'}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t shrink-0">
              <button
                onClick={submitBulkPriceHistory}
                disabled={bulkHistorySubmitting || !bulkHistoryDate}
                className="w-full py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
              >{bulkHistorySubmitting ? 'Saving...' : 'Save All Prices'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
